/* User settings. Preferences persist on the account (server-side) and sync across
 * devices. Currently: the UI theme and the reduce-motion accessibility toggle. */
import { useNavigate } from 'react-router-dom';
import type { Theme } from '@venturewrist/shared';
import { useAuth, useUpdatePreferences } from '../hooks/queries';
import { useReduceMotion } from '../hooks/useReduceMotion';
import { useTheme } from '../hooks/useTheme';

/* Swatch previews. These are literals on purpose: each one has to show a theme
 * the page is *not* currently rendering in, so it can't read the live tokens. */
const THEMES: { id: Theme; name: string; blurb: string; swatch: [string, string, string] }[] = [
  { id: 'dark', name: 'Dark', blurb: 'The original navy console.', swatch: ['#0a0f1e', '#131c3a', '#22d3ee'] },
  { id: 'light', name: 'Light', blurb: 'Warm paper, in the house style.', swatch: ['#fbf8f3', '#ffffff', '#ff4f24'] },
];

export function Settings() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const update = useUpdatePreferences();
  // Both switches reflect the *effective* state (explicit setting, else the OS
  // default); choosing writes an explicit value that overrides the OS.
  const checked = useReduceMotion();
  const followingOs = user?.reduceMotion == null;
  const theme = useTheme();
  const usingDefault = user?.theme == null;

  return (
    <div className="mx-auto max-w-2xl">
      <button onClick={() => navigate(-1)} className="mb-4 text-sm text-mut hover:text-txt">← Back</button>
      <h1 className="text-2xl font-extrabold tracking-tight">Settings</h1>
      <p className="mt-1 text-sm text-mut">Preferences are saved to your account and apply on every device.</p>

      <div className="card mt-5 p-5">
        <h2 className="label mb-0 text-sm font-semibold uppercase">Appearance</h2>
        <div className="mt-1 text-sm text-mut">
          Pick how VentureWrist looks.
          {usingDefault && ' Light is the default — choose either to set your own.'}
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {THEMES.map((t) => {
            const active = theme === t.id;
            return (
              <button
                key={t.id}
                type="button"
                aria-pressed={active}
                disabled={update.isPending}
                onClick={() => update.mutate({ theme: t.id })}
                className={`flex items-center gap-3 rounded-sm border p-3 text-left transition-colors disabled:opacity-60 ${
                  active ? 'border-acc bg-acc/10' : 'border-line hover:border-[color:var(--line-strong)]'
                }`}
              >
                {/* Previews a theme the page is not currently in, so it is the
                    one place that must NOT follow the active tokens. The e2e
                    theme sweep skips it by the data-theme-preview attribute. */}
                <span
                  aria-hidden="true"
                  data-theme-preview={t.id}
                  className="grid h-10 w-10 shrink-0 grid-cols-2 overflow-hidden rounded-sm border border-line"
                  style={{ background: t.swatch[0] }}
                >
                  <span style={{ background: t.swatch[1], gridColumn: '1 / -1', height: '55%' }} />
                  <span style={{ background: t.swatch[2], height: 6, alignSelf: 'center', margin: '0 4px', borderRadius: 3 }} />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold">{t.name}</span>
                  <span className="block text-xs text-mut">{t.blurb}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="card mt-4 p-5">
        <h2 className="label mb-0 text-sm font-semibold uppercase">Accessibility</h2>
        <div className="mt-3 flex items-center justify-between gap-4">
          <div>
            <div className="font-medium">Reduce motion</div>
            <div className="text-sm text-mut">
              Minimize animation in charts, the supply-chain map, and UI transitions.
              {followingOs && checked && ' Following your system setting — toggle to override.'}
            </div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={checked}
            aria-label="Reduce motion"
            disabled={update.isPending}
            onClick={() => update.mutate({ reduceMotion: !checked })}
            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${checked ? 'bg-acc' : 'bg-[color:var(--line-strong)]'}`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-[color:var(--thumb)] shadow transition-transform ${checked ? 'translate-x-[22px]' : 'translate-x-0.5'}`}
            />
          </button>
        </div>
        {update.isError && <p className="mt-3 text-sm text-bad">Couldn't save that change — please try again.</p>}
      </div>
    </div>
  );
}
